/**
 * TagsComboBox — creatable multi-select for test case tags.
 *
 * Fetches existing tag suggestions from GET /api/test-cases/meta.
 * Selected tags render as dismissible chips above the input.
 * Press Enter or comma to add a new tag. Backspace on empty input removes the last tag.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { X, Tag } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../../context/AuthContext';
import { API_BASE_URL } from '../../../config/api';

const API_URL = API_BASE_URL;

interface TagsComboBoxProps {
    value:     string[];
    onChange:  (tags: string[]) => void;
    projectId: string | null;
}

export function TagsComboBox({ value, onChange, projectId }: TagsComboBoxProps) {
    const { token }                           = useAuth();
    const [inputValue,    setInputValue]      = useState('');
    const [suggestions,   setSuggestions]     = useState<string[]>([]);
    const [allTags,       setAllTags]         = useState<string[]>([]);
    const [dropdownOpen,  setDropdownOpen]    = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef     = useRef<HTMLInputElement>(null);

    // Fetch tag suggestions once (or when project changes)
    useEffect(() => {
        if (!token || !projectId) return;
        axios
            .get(`${API_URL}/api/test-cases/meta`, {
                params: { projectId },
                headers: { Authorization: `Bearer ${token}` },
            })
            .then(({ data }) => {
                if (data.success && Array.isArray(data.data?.tags)) {
                    setAllTags(data.data.tags as string[]);
                }
            })
            .catch(() => { /* silent */ });
    }, [token, projectId]);

    // Filter suggestions based on input + exclude already-selected tags
    useEffect(() => {
        const q = inputValue.trim().toLowerCase();
        const filtered = allTags
            .filter((t) => !value.includes(t))
            .filter((t) => !q || t.toLowerCase().includes(q));
        setSuggestions(filtered);
    }, [inputValue, allTags, value]);

    // Close dropdown on outside click
    useEffect(() => {
        if (!dropdownOpen) return;
        const handler = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [dropdownOpen]);

    const addTag = useCallback((tag: string) => {
        const trimmed = tag.trim().toLowerCase().replace(/,/g, '');
        if (!trimmed || value.includes(trimmed)) return;
        onChange([...value, trimmed]);
        setInputValue('');
        setDropdownOpen(false);
    }, [value, onChange]);

    const removeTag = useCallback((tag: string) => {
        onChange(value.filter((t) => t !== tag));
    }, [value, onChange]);

    function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
        if ((e.key === 'Enter' || e.key === ',') && inputValue.trim()) {
            e.preventDefault();
            addTag(inputValue);
        }
        if (e.key === 'Backspace' && !inputValue && value.length > 0) {
            onChange(value.slice(0, -1));
        }
        if (e.key === 'Escape') {
            setDropdownOpen(false);
            inputRef.current?.blur();
        }
    }

    return (
        <div ref={containerRef} data-testid="tags-combobox" className="relative">
            {/* Tag chips + input */}
            <div
                data-testid="tags-combobox-input-area"
                className="min-h-[36px] flex flex-wrap gap-1 items-center px-2 py-1.5 border border-slate-200 dark:border-gh-border-dark rounded-lg bg-white dark:bg-gh-bg-dark cursor-text focus-within:ring-2 focus-within:ring-blue-500 transition"
                onClick={() => inputRef.current?.focus()}
            >
                <Tag size={12} className="text-slate-400 flex-shrink-0" />

                {value.map((tag) => (
                    <span
                        key={tag}
                        className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-xs bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400"
                    >
                        {tag}
                        <button
                            type="button"
                            aria-label={`Remove tag ${tag}`}
                            onClick={(e) => { e.stopPropagation(); removeTag(tag); }}
                            className="ml-0.5 hover:text-emerald-900 dark:hover:text-emerald-200 transition-colors"
                        >
                            <X size={10} />
                        </button>
                    </span>
                ))}

                <input
                    ref={inputRef}
                    aria-label="Add tags"
                    data-testid="tags-combobox-input"
                    value={inputValue}
                    onChange={(e) => { setInputValue(e.target.value); setDropdownOpen(true); }}
                    onFocus={() => setDropdownOpen(true)}
                    onKeyDown={handleKeyDown}
                    placeholder={value.length === 0 ? 'Add tags…' : ''}
                    className="flex-1 min-w-[80px] text-xs bg-transparent border-none outline-none text-slate-800 dark:text-slate-200 placeholder-slate-400"
                />
            </div>

            {/* Dropdown */}
            {dropdownOpen && (suggestions.length > 0 || inputValue.trim()) && (
                <div data-testid="tags-combobox-dropdown" className="absolute top-full mt-1 left-0 right-0 bg-white dark:bg-slate-800 border border-slate-200 dark:border-gh-border-dark rounded-lg shadow-lg z-50 max-h-40 overflow-y-auto">
                    {/* "Create new" option when input has value */}
                    {inputValue.trim() && !suggestions.includes(inputValue.trim().toLowerCase()) && (
                        <button
                            type="button"
                            data-testid="tags-combobox-create-button"
                            onMouseDown={(e) => { e.preventDefault(); addTag(inputValue); }}
                            className="w-full text-left px-3 py-1.5 text-xs text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                        >
                            Create tag "<strong>{inputValue.trim()}</strong>"
                        </button>
                    )}
                    {suggestions.map((tag) => (
                        <button
                            key={tag}
                            type="button"
                            data-testid={`tags-combobox-suggestion-${tag}`}
                            onMouseDown={(e) => { e.preventDefault(); addTag(tag); }}
                            className="w-full text-left px-3 py-1.5 text-xs text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                        >
                            {tag}
                        </button>
                    ))}
                </div>
            )}

            <p className="text-[10px] text-slate-400 mt-1">Press Enter or comma to add · Backspace to remove last</p>
        </div>
    );
}
